/**
 * Scan results page
 * Classification results table with filters, badges, and export
 */

import { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, RefreshCw, AlertCircle, Sparkles, FileText, Wand2, Loader2, Database } from 'lucide-react'
import { toast } from 'sonner'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MetricCard } from '@/components/dashboard'
import {
  ClassificationBadge,
  RegulatoryFlagList,
  ScanStatusBadge,
} from '@/components/discovery'
import { useScan, useScanResults } from '@/hooks/use-discovery'
import { useGeneratePolicies } from '@/hooks/use-generate-policies'
import { useSyncFromDiscovery } from '@/hooks/use-catalog-sync'
import { sanitizeCSVCell } from '@/lib/discovery-utils'
import { buildPolixAIUrl } from '@/lib/cross-app-context'
import { cn } from '@/lib/utils'
import type {
  ScanResultsQueryParams,
  ClassificationLevel,
  ClassificationMethod,
  DiscoveryResult,
} from '@/types/discovery'

function hasITAREAR(result: DiscoveryResult): boolean {
  return result.regulatoryFlags.some(
    (f) => f === 'ITAR' || f === 'EAR'
  )
}

function exportToCSV(results: readonly DiscoveryResult[], scanId: string) {
  const headers = [
    'Schema',
    'Table',
    'Column',
    'Data Type',
    'Classification',
    'Level',
    'Confidence',
    'Method',
    'Regulatory Flags',
    'Requires Review',
  ]
  const rows = results.map((r) => [
    r.schemaName,
    r.tableName,
    r.columnName,
    r.dataType,
    r.classificationLabel,
    r.classificationLevel,
    String(Math.round(r.confidence * 100)),
    r.method,
    r.regulatoryFlags.join('; '),
    r.requiresReview ? 'Yes' : 'No',
  ])

  const csv = [
    headers.join(','),
    ...rows.map((row) => row.map(sanitizeCSVCell).join(',')),
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `scan-results-${scanId.slice(0, 8)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export function ScanResultsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: scan, isLoading: scanLoading, isError, error } = useScan(id)

  const [filters, setFilters] = useState<ScanResultsQueryParams>({
    page: 1,
    pageSize: 50,
  })
  const [search, setSearch] = useState('')

  const {
    data: resultsData,
    isLoading: resultsLoading,
    refetch,
  } = useScanResults(id, filters)

  const generatePolicies = useGeneratePolicies()
  const syncFromDiscovery = useSyncFromDiscovery()

  const results = resultsData?.data ?? []
  const pagination = resultsData?.pagination

  // Client-side search filter on schema.table.column
  const filteredResults = search
    ? results.filter((r) => {
        const fullPath =
          `${r.schemaName}.${r.tableName}.${r.columnName}`.toLowerCase()
        return fullPath.includes(search.toLowerCase())
      })
    : results

  const handleLevelFilter = useCallback(
    (value: string) => {
      setFilters({
        ...filters,
        page: 1,
        classificationLevel:
          value === 'all' ? undefined : (value as ClassificationLevel),
      })
    },
    [filters]
  )

  const handleMethodFilter = useCallback(
    (value: string) => {
      setFilters({
        ...filters,
        page: 1,
        method: value === 'all' ? undefined : (value as ClassificationMethod),
      })
    },
    [filters]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters({ ...filters, page: newPage })
    },
    [filters]
  )

  const handleExport = useCallback(() => {
    if (id && results.length > 0) {
      exportToCSV(results, id)
    }
  }, [id, results])

  const handleGeneratePolicies = useCallback(() => {
    if (!id) return

    generatePolicies.mutate(id, {
      onSuccess: (data) => {
        toast.success(`Generated ${data.count} policy draft${data.count === 1 ? '' : 's'} from scan results`)
        navigate('/pac/policies')
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : 'Failed to generate policies'
        toast.error(message)
      },
    })
  }, [id, generatePolicies, navigate])

  const isCompleted = scan?.status === 'COMPLETED'
  const canGeneratePolicies = isCompleted && results.length > 0

  const handleSyncToCatalog = useCallback(() => {
    if (!scan || !id) return
    syncFromDiscovery.mutate({
      scanId: id,
      applicationId: scan.connectionId,
    })
  }, [scan, id, syncFromDiscovery])

  if (scanLoading) {
    return (
      <>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Skeleton className="h-5 w-48" />
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </>
    )
  }

  if (isError || !scan) {
    return (
      <>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={() => navigate('/discovery/scans')}
          >
            <ArrowLeft className="mr-2 h-3.5 w-3.5" />
            Back
          </Button>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : 'Scan not found'}
            </AlertDescription>
          </Alert>
        </div>
      </>
    )
  }

  const needsReviewCount = results.filter((r) => r.requiresReview).length

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={() => navigate('/discovery/scans')}
          >
            <ArrowLeft className="mr-2 h-3.5 w-3.5" />
            Back
          </Button>
          <h1 className="text-base font-medium">Scan Results</h1>
          <ScanStatusBadge status={scan.status} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => refetch()}
            disabled={resultsLoading}
          >
            <RefreshCw
              className={resultsLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
            />
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-8"
            onClick={handleGeneratePolicies}
            disabled={!canGeneratePolicies || generatePolicies.isPending}
            title={
              !isCompleted
                ? 'Scan must be completed first'
                : results.length === 0
                  ? 'No results to generate policies from'
                  : 'Auto-generate XACML policies from scan results'
            }
          >
            {generatePolicies.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="mr-2 h-3.5 w-3.5" />
            )}
            {generatePolicies.isPending ? 'Generating...' : 'Generate Policies'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => {
              if (!id) return
              const connectionName = scan.connection?.name ?? 'scan'
              navigate(buildPolixAIUrl({ scanId: id, connectionName }))
            }}
            disabled={results.length === 0}
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Ask AI
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => navigate('/pac/policies/new')}
            disabled={results.length === 0}
          >
            <FileText className="mr-2 h-3.5 w-3.5" />
            Create Policy
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={handleExport}
            disabled={results.length === 0}
          >
            <Download className="mr-2 h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Summary Cards */}
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Columns"
            value={scan.columnsScanned}
            description="Scanned in this run"
            isLoading={false}
          />
          <MetricCard
            title="Classified"
            value={scan.columnsClassified}
            description="Successfully classified"
            isLoading={false}
          />
          <MetricCard
            title="Flagged"
            value={results.filter((r) => r.regulatoryFlags.length > 0).length}
            description="Sensitive data detected"
            isLoading={false}
          />
          <MetricCard
            title="Need Review"
            value={needsReviewCount}
            description="Requires human verification"
            isLoading={false}
          />
        </div>

        {/* Sync to Catalog CTA — shown when scan is COMPLETED */}
        {isCompleted && results.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
            <div className="flex items-center gap-3">
              <Database className="h-4 w-4 text-primary shrink-0" />
              <div>
                <p className="text-sm font-medium">Sync results to Data Catalog</p>
                <p className="text-xs text-muted-foreground">
                  Import {scan.columnsClassified} classified columns into the catalog to create XACML policies.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="default"
              className="ml-4 shrink-0"
              onClick={handleSyncToCatalog}
              disabled={syncFromDiscovery.isPending}
            >
              {syncFromDiscovery.isPending ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Database className="mr-2 h-3.5 w-3.5" />
              )}
              {syncFromDiscovery.isPending ? 'Syncing...' : 'Sync to Catalog'}
            </Button>
          </div>
        )}

        {/* Error message if scan failed */}
        {scan.status === 'FAILED' && scan.errorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{scan.errorMessage}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Input
            placeholder="Search schema.table.column..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 max-w-xs"
          />
          <Select
            value={filters.classificationLevel ?? 'all'}
            onValueChange={handleLevelFilter}
          >
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="PUBLIC">Public</SelectItem>
              <SelectItem value="INTERNAL">Internal</SelectItem>
              <SelectItem value="CONFIDENTIAL">Confidential</SelectItem>
              <SelectItem value="RESTRICTED">Restricted</SelectItem>
              <SelectItem value="TOP_SECRET">Top Secret</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.method ?? 'all'}
            onValueChange={handleMethodFilter}
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue placeholder="Method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="RULE_BASED">Rule-Based</SelectItem>
              <SelectItem value="AI_ASSISTED">AI-Assisted</SelectItem>
              <SelectItem value="MANUAL">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading */}
        {resultsLoading && (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {/* Empty */}
        {!resultsLoading && filteredResults.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">
              {search || filters.classificationLevel || filters.method
                ? 'No results match your filters.'
                : scan.status === 'PENDING' || scan.status === 'RUNNING'
                  ? 'Scan is still in progress. Results will appear when ready.'
                  : 'No classification results found.'}
            </p>
          </div>
        )}

        {/* Results Table */}
        {!resultsLoading && filteredResults.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Schema.Table.Column</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead className="text-right">Confidence</TableHead>
                  <TableHead>Method</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResults.map((result) => (
                  <TableRow
                    key={result.id}
                    className={cn(hasITAREAR(result) && 'bg-red-500/5')}
                  >
                    <TableCell className="font-mono text-sm">
                      <span className="text-muted-foreground">
                        {result.schemaName}.{result.tableName}.
                      </span>
                      <span className="font-medium">{result.columnName}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {result.dataType}
                    </TableCell>
                    <TableCell>
                      <ClassificationBadge level={result.classificationLevel} />
                    </TableCell>
                    <TableCell>
                      <RegulatoryFlagList flags={result.regulatoryFlags} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Math.round(result.confidence * 100)}%
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {result.method === 'RULE_BASED'
                        ? 'Rule'
                        : result.method === 'AI_ASSISTED'
                          ? 'AI'
                          : 'Manual'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredResults.length} of {pagination.totalItems}{' '}
                  result{pagination.totalItems === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPreviousPage}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
